import React, { Component } from 'react';
import AdminWrapper from './AdminWrapper';
import { adminService } from '../../_services/admin.service';

// AdminReconciliations — the audit feed of every action the money
// reconciliation sweeps took: product payout retries, missed-webhook order
// finalisations, gift-card issues. Recovered rows show what was rescued;
// failed rows show WHY (Stripe error etc.) so support can act.

const KIND_LABEL = {
  product_payout: 'Product payout retry',
  product_finalise: 'Order finalised (webhook missed)',
  gift_card_issue: 'Gift card issued (webhook missed)',
  voucher_restore: 'Gift card restored (abandoned booking)',
  deposit_refund: 'Deposit refunded (salon cancelled)',
  order_refund: 'Order refunded (salon cancelled)',
};

const PAGE_SIZE = 50;

class AdminReconciliations extends Component {
  state = {
    events: [],
    total: 0,
    loading: true,
    outcome: '', // '' | 'recovered' | 'failed'
    error: null,
  };

  componentDidMount() {
    this.fetchEvents(0, false);
  }

  fetchEvents = async (skip = 0, append = false) => {
    this.setState({ loading: true, error: null });
    try {
      const res = await adminService.getReconciliations({
        skip,
        limit: PAGE_SIZE,
        outcome: this.state.outcome || undefined,
      });
      const data = (res && res.responseData) || res || {};
      this.setState((prev) => ({
        events: append ? [...prev.events, ...(data.events || [])] : (data.events || []),
        total: data.total || 0,
        loading: false,
      }));
    } catch (e) {
      console.error('Error fetching reconciliations:', e);
      this.setState({ loading: false, error: 'Failed to load reconciliation events' });
    }
  };

  setOutcome = (outcome) => {
    this.setState({ outcome }, () => this.fetchEvents(0, false));
  };

  render() {
    const { events, total, loading, outcome, error } = this.state;

    return (
      <AdminWrapper title="Reconciliations">
        <div className="admin-page-header">
          <div>
            <h1 className="admin-page-title">Reconciliations</h1>
            <p className="admin-page-subtitle">
              Every action the money sweeps took — recovered payouts, orders finalised after a
              missed webhook, gift cards issued late. Empty is good news.
            </p>
          </div>
        </div>

        <div className="admin-filters" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[['', 'All'], ['recovered', 'Recovered'], ['failed', 'Failed']].map(([val, label]) => (
            <button
              key={val || 'all'}
              type="button"
              onClick={() => this.setOutcome(val)}
              style={{
                padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: '1px solid ' + (outcome === val ? '#29282D' : '#e0e0e0'),
                background: outcome === val ? '#29282D' : '#fff',
                color: outcome === val ? '#fff' : '#444',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {error && <div style={{ color: '#b42318', marginBottom: 12 }}>{error}</div>}

        {loading && events.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading…</div>
        ) : events.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#667085', background: '#fafafa', borderRadius: 12 }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}><span role="img" aria-label="All clear">✅</span></div>
            <strong>No reconciliation events{outcome ? ` (${outcome})` : ''}</strong>
            <div style={{ fontSize: 13, marginTop: 6 }}>
              The sweeps run every 5 minutes and haven't needed to rescue anything.
            </div>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Action</th>
                  <th>Outcome</th>
                  <th>Reference</th>
                  <th>Salon</th>
                  <th>Amount</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e._id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{new Date(e.at).toLocaleString('en-GB')}</td>
                    <td>{KIND_LABEL[e.kind] || e.kind}</td>
                    <td>
                      <span style={{
                        padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                        background: e.outcome === 'recovered' ? '#ECFDF3' : '#FEF3F2',
                        color: e.outcome === 'recovered' ? '#067647' : '#b42318',
                      }}>
                        {e.outcome}
                      </span>
                    </td>
                    <td style={{ fontFamily: 'Menlo, monospace', fontSize: 12 }}>{e.reference || '—'}</td>
                    <td>{e.salonName || '—'}</td>
                    <td>{e.amount != null ? `£${Number(e.amount).toFixed(2)}` : '—'}</td>
                    <td style={{ maxWidth: 360, fontSize: 12, color: e.error ? '#b42318' : '#667085' }}>
                      {e.error || e.note || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {events.length < total && (
              <div style={{ padding: 14, textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => this.fetchEvents(events.length, true)}
                  disabled={loading}
                  style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', fontWeight: 600, cursor: 'pointer' }}
                >
                  {loading ? 'Loading…' : `Load more (${total - events.length} remaining)`}
                </button>
              </div>
            )}
          </div>
        )}
      </AdminWrapper>
    );
  }
}

export default AdminReconciliations;
